import {
  useState,
  useCallback,
  useEffect,
} from "react";

import {
  ChevronLeft,
  ChevronRight,
  Play,
  Tv,
  CalendarDays,
} from "lucide-react";

import { Link } from "react-router-dom";

import useEmblaCarousel from "embla-carousel-react";

import { CircularProgress } from "@mui/material";

import apiClient from "../../utils/apiClient";
import { imageBaseurl } from "../../utils/common";

export function ImageSlider() {

  const [events, setEvents] =
    useState([]);

  const [loadingEvents, setLoadingEvents] =
    useState(false);

  const [selectedIndex, setSelectedIndex] =
    useState(0);

  // ============================
  // API
  // ============================
  const getEvents = async () => {

    try {

      setLoadingEvents(true);

      const response =
        await apiClient.get(
          "/public/slider"
        );

      const result =
        response?.data;

      if (result?.success) {

        setEvents(
          result.data || []
        );
      }

    } catch (error) {

      console.log(
        "Slider API Error:",
        error
      );

    } finally {

      setLoadingEvents(false);
    }
  };

  useEffect(() => {
    getEvents();
  }, []);

  // ============================
  // EMBLA
  // ============================
  const [emblaRef, emblaApi] =
    useEmblaCarousel({
      loop: true,
    });

  const scrollPrev =
    useCallback(() => {

      emblaApi?.scrollPrev();

    }, [emblaApi]);

  const scrollNext =
    useCallback(() => {

      emblaApi?.scrollNext();

    }, [emblaApi]);

  const scrollTo =
    useCallback(
      (i) =>
        emblaApi?.scrollTo(i),
      [emblaApi]
    );

  // ============================
  // SELECT
  // ============================
  useEffect(() => {

    if (!emblaApi) return;

    const onSelect = () => {

      setSelectedIndex(
        emblaApi.selectedScrollSnap()
      );
    };

    emblaApi.on(
      "select",
      onSelect
    );

    onSelect();

    const autoplay =
      setInterval(() => {

        emblaApi.scrollNext();

      }, 6000);

    return () => {

      clearInterval(
        autoplay
      );

      emblaApi.off(
        "select",
        onSelect
      );
    };

  }, [emblaApi]);

  // ============================
  // COUNTDOWN
  // ============================
  const getCountdown = (
    date
  ) => {

    if (!date)
      return null;

    const now =
      new Date().getTime();

    const target =
      new Date(
        date
      ).getTime();

    const distance =
      target - now;

    if (distance <= 0)
      return "Live Now";

    const days =
      Math.floor(
        distance /
        (1000 *
          60 *
          60 *
          24)
      );

    const hours =
      Math.floor(
        (distance %
          (1000 *
            60 *
            60 *
            24)) /
        (1000 *
          60 *
          60)
      );

    const minutes =
      Math.floor(
        (distance %
          (1000 *
            60 *
            60)) /
        (1000 *
          60)
      );

    return `${days}d ${hours}h ${minutes}m`;
  };

  // ============================
  // LOADING
  // ============================
  if (loadingEvents) {

    return (
      <div className="flex justify-center py-10">
        <CircularProgress />
      </div>
    );
  }

  // ============================
  // EMPTY
  // ============================
  if (
    events?.length === 0
  ) {

    return null;
  }

  return (

    <section className="relative w-full overflow-hidden rounded-3xl shadow-2xl mb-6">

      {/* SLIDER */}

      <div
        ref={emblaRef}
        className="overflow-hidden"
      >

        <div className="flex">

          {events.map(
            (
              slide,
              i
            ) => (

              <div
                key={i}
                className="
                                relative
                                min-w-0
                                flex-[0_0_100%]
                                "
              >

                <div className="relative aspect-[16/9] md:aspect-[21/8] overflow-hidden">

                  {/* IMAGE */}

                  <img
                    src={
                      imageBaseurl + slide.image
                    }
                    alt={
                      slide.title
                    }
                    className="
                                        absolute
                                        inset-0
                                        w-full
                                        h-full
                                        transition-transform
                                        duration-700
                                        scale-105
                                        hover:scale-110
                                        "
                  />

                  {/* OVERLAY */}

                  <div
                    className="
                                        absolute
                                        inset-0
                                        bg-gradient-to-t
                                        from-black/95
                                        via-black/40
                                        to-black/20
                                        "
                  />

                  {/* LIVE BADGE */}

                  {slide.is_live && (

                    <div
                      className="
                                            absolute
                                            top-4
                                            left-4
                                            z-20
                                            flex
                                            items-center
                                            gap-2
                                            rounded-full
                                            bg-red-600
                                            px-4
                                            py-2
                                            text-sm
                                            font-bold
                                            text-white
                                            shadow-lg
                                            animate-pulse
                                            "
                    >

                      <span className="h-2 w-2 rounded-full bg-white" />

                      LIVE

                      <Tv className="w-4 h-4" />

                    </div>
                  )}

                  {/* COUNTDOWN */}

                  {/* {slide.live_start_time && (

                                        <div
                                            className="
                                            absolute
                                            top-4
                                            right-4
                                            z-20
                                            flex
                                            items-center
                                            gap-2
                                            rounded-full
                                            bg-black/60
                                            backdrop-blur-md
                                            border
                                            border-white/20
                                            px-4
                                            py-2
                                            text-sm
                                            font-semibold
                                            text-white
                                            "
                                        >

                                            <CalendarDays className="w-4 h-4 text-orange-400" />

                                            {
                                                getCountdown(
                                                    slide.live_start_time
                                                )
                                            }

                                        </div>
                                    )} */}
                  {
                    slide.live_start_time && (
                      <div className="absolute top-4 right-4 z-20">
                        <Countdown
                          targetDate={
                            slide.live_start_time
                          }
                        />
                      </div>
                    )
                  }
                  {/* CONTENT */}

                  <div
                    className="
                                        absolute
                                        bottom-0
                                        left-0
                                        z-10
                                        w-full
                                        p-5
                                        md:p-10
                                        "
                  >

                    <div className="max-w-3xl animate-slideUp">

                      {/* SPORT */}

                      <div
                        className="
                                                inline-flex
                                                items-center
                                                rounded-full
                                                bg-white/10
                                                backdrop-blur-md
                                                border
                                                border-white/20
                                                px-4
                                                py-1.5
                                                text-sm
                                                font-semibold
                                                text-white
                                                mb-4
                                                "
                      >

                        {
                          slide.sport_type
                        }{" "}

                        •{" "}

                        {
                          slide.year
                        }

                      </div>

                      {/* TITLE */}

                      <h2
                        className="
                                                text-2xl
                                                md:text-5xl
                                                font-black
                                                text-white
                                                leading-tight
                                                mb-4
                                                "
                      >

                        {
                          slide.title
                        }

                      </h2>

                      {/* DESCRIPTION */}

                      <p
                        className="
                                                text-sm
                                                md:text-lg
                                                text-gray-200
                                                max-w-2xl
                                                mb-6
                                                line-clamp-2
                                                "
                      >

                        {
                          slide.short_description
                        }

                      </p>

                      {/* BUTTONS */}
                      {(slide.button_url && slide.button_text) &&
                        <div className="flex flex-wrap gap-4">

                          <Link
                            to={
                              slide.button_url ||
                              slide.redirect_url
                            }
                          >

                            <button
                              className="
                                                        inline-flex
                                                        items-center
                                                        gap-2
                                                        rounded-xl
                                                        bg-gradient-to-r
                                                        from-red-600
                                                        to-orange-500
                                                        px-6
                                                        py-3
                                                        text-sm
                                                        md:text-base
                                                        font-bold
                                                        text-white
                                                        shadow-lg
                                                        transition-all
                                                        hover:scale-105
                                                        "
                            >

                              <Play className="w-4 h-4 fill-white" />

                              {
                                slide.button_text ||
                                "Watch Now"
                              }

                            </button>

                          </Link>

                        </div>
                      }

                    </div>

                  </div>

                </div>

              </div>
            )
          )}

        </div>

      </div>

      {/* PREV */}

      <button
        onClick={scrollPrev}
        className="
                absolute
                left-4
                top-1/2
                -translate-y-1/2
                z-30
                flex
                h-12
                w-12
                items-center
                justify-center
                rounded-full
                bg-black/40
                backdrop-blur-md
                border
                border-white/20
                text-white
                transition-all
                hover:bg-black/70
                "
      >

        <ChevronLeft className="w-6 h-6" />

      </button>

      {/* NEXT */}

      <button
        onClick={scrollNext}
        className="
                absolute
                right-4
                top-1/2
                -translate-y-1/2
                z-30
                flex
                h-12
                w-12
                items-center
                justify-center
                rounded-full
                bg-black/40
                backdrop-blur-md
                border
                border-white/20
                text-white
                transition-all
                hover:bg-black/70
                "
      >

        <ChevronRight className="w-6 h-6" />

      </button>

      {/* DOTS */}

      <div
        className="
                absolute
                bottom-4
                left-1/2
                -translate-x-1/2
                z-30
                flex
                gap-2
                "
      >

        {events.map(
          (_, i) => (

            <button
              key={i}
              onClick={() =>
                scrollTo(
                  i
                )
              }
              className={`
                            h-2.5
                            rounded-full
                            transition-all
                            duration-300

                            ${i ===
                  selectedIndex
                  ? "w-8 bg-orange-500"
                  : "w-2.5 bg-white/40"
                }
                            `}
            />
          )
        )}

      </div>

      {/* ANIMATION */}

      <style jsx>{`

                .animate-slideUp {

                    animation:
                        slideUp
                        0.7s
                        ease-out;
                }

                @keyframes slideUp {

                    0% {

                        opacity: 0;

                        transform:
                            translateY(
                                40px
                            );
                    }

                    100% {

                        opacity: 1;

                        transform:
                            translateY(
                                0
                            );
                    }
                }

            `}</style>

    </section>
  );
}


// ============================
// COUNTDOWN COMPONENT
// ============================
function Countdown({
  targetDate,
}) {

  const calculateTimeLeft = () => {

    const difference =
      new Date(
        targetDate
      ).getTime() -
      new Date().getTime();

    if (difference <= 0) {

      return {
        expired: true,
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
      };
    }

    return {

      expired: false,

      days: Math.floor(
        difference /
        (1000 *
          60 *
          60 *
          24)
      ),

      hours: Math.floor(
        (difference %
          (1000 *
            60 *
            60 *
            24)) /
        (1000 *
          60 *
          60)
      ),

      minutes: Math.floor(
        (difference %
          (1000 *
            60 *
            60)) /
        (1000 *
          60)
      ),

      seconds: Math.floor(
        (difference %
          (1000 *
            60)) /
        1000
      ),
    };
  };

  const [timeLeft, setTimeLeft] =
    useState(
      calculateTimeLeft()
    );

  useEffect(() => {

    const timer =
      setInterval(() => {

        setTimeLeft(
          calculateTimeLeft()
        );

      }, 1000);

    return () =>
      clearInterval(timer);

  }, [targetDate]);

  // ============================
  // LIVE NOW
  // ============================
  if (timeLeft.expired) {
return null
    return (

      <div
        className="
                flex
                items-center
                gap-2
                rounded-full
                bg-red-600
                px-4
                py-2
                text-sm
                font-bold
                text-white
                shadow-lg
                "
      >

        <span className="h-2 w-2 rounded-full bg-white animate-pulse" />

        LIVE NOW

      </div>
    );
  }

  // ============================
  // COUNTDOWN UI
  // ============================
  return (

    <div
      className="
            flex
            items-center
            gap-2
            rounded-2xl
            bg-black/50
            backdrop-blur-md
            border
            border-white/20
            px-3
            py-2
            text-white
            shadow-xl
            "
    >

      {/* DAYS */}

      <div className="text-center min-w-[55px]">

        <h4 className="text-lg md:text-xl font-black text-orange-400">
          {String(
            timeLeft.days
          ).padStart(2, "0")}
        </h4>

        <p className="text-[10px] uppercase text-white/70">
          Days
        </p>

      </div>

      <span className="text-white/40">
        :
      </span>

      {/* HOURS */}

      <div className="text-center min-w-[55px]">

        <h4 className="text-lg md:text-xl font-black text-orange-400">
          {String(
            timeLeft.hours
          ).padStart(2, "0")}
        </h4>

        <p className="text-[10px] uppercase text-white/70">
          Hours
        </p>

      </div>

      <span className="text-white/40">
        :
      </span>

      {/* MINUTES */}

      <div className="text-center min-w-[55px]">

        <h4 className="text-lg md:text-xl font-black text-orange-400">
          {String(
            timeLeft.minutes
          ).padStart(2, "0")}
        </h4>

        <p className="text-[10px] uppercase text-white/70">
          Minutes
        </p>

      </div>

      <span className="text-white/40">
        :
      </span>

      {/* SECONDS */}

      <div className="text-center min-w-[55px]">

        <h4 className="text-lg md:text-xl font-black text-red-500 animate-pulse">
          {String(
            timeLeft.seconds
          ).padStart(2, "0")}
        </h4>

        <p className="text-[10px] uppercase text-white/70">
          Seconds
        </p>

      </div>

    </div>
  );
}

